/**
 * Transient in-app banner for a session-event push received while foregrounded.
 * Same useSyncExternalStore pattern as `message-notification-store`.
 */

import { useSyncExternalStore } from 'react'

export type SessionPushBanner = {
  id: string
  title: string
  body: string
  route: string
}

const AUTO_DISMISS_MS = 10_000

let current: SessionPushBanner | null = null
let hideTimer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

function emit() {
  for (const listener of listeners) listener()
}

export function showSessionPushBanner(banner: Omit<SessionPushBanner, 'id'>): void {
  current = { ...banner, id: `${Date.now()}-${banner.route}` }
  emit()
  if (hideTimer) clearTimeout(hideTimer)
  hideTimer = setTimeout(() => dismissSessionPushBanner(), AUTO_DISMISS_MS)
}

export function dismissSessionPushBanner(): void {
  if (hideTimer) {
    clearTimeout(hideTimer)
    hideTimer = null
  }
  if (!current) return
  current = null
  emit()
}

export function getSessionPushBanner(): SessionPushBanner | null {
  return current
}

export function subscribeSessionPushBanner(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function useSessionPushBanner(): SessionPushBanner | null {
  return useSyncExternalStore(subscribeSessionPushBanner, getSessionPushBanner, () => null)
}
